import { useEffect, useState, useRef, useMemo } from "react";
import {
    VStack,
    HStack,
    Box,
    Text,
    Heading,
    Avatar,
    Input,
    InputGroup,
    InputLeftElement,
    IconButton,
    Button,
    Flex,
    Divider,
    Center,
    Spinner,
    useColorModeValue,
    useToast,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Badge,
    Image,
    Portal,
    Grid,
    GridItem
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPaperPlane,
    faTrash,
    faEllipsisV,
    faUsers,
    faTimes,
    faExclamationTriangle,
    faSmile,
    faExpandAlt,
    faCompressAlt,
    faExternalLinkAlt,
    faSearch
} from "@fortawesome/free-solid-svg-icons";
import { apiGetRequest, apiPostRequest, apiDeleteRequest } from "@/functions/requests";
import { useAuthenticator } from "@aws-amplify/ui-react";
import PRESET_GIFS from "@/data/gifs.json";

interface ChatUser {
    email: string;
    role: string;
    org: string;
    status: string;
    nickname?: string;
    picture?: string;
    avatarUrl?: string; // Resolved URL
}

interface ChatMessage {
    chatId: string;
    timestamp: number;
    senderEmail: string;
    senderName: string;
    message: string;
    imageUrl?: string;
    sharedLog?: string;
    fileUrl?: string;
    fileName?: string;
    fileType?: string;
}

interface ChatReadSummary {
    chatId: string;
    lastReadTimestamp: number;
    latestMessageTimestamp: number;
}

const EMOJIS = [
    "😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "😊", "😇", "🙂", "🙃", "😉", "😌", "😍", "🥰",
    "😘", "😗", "😙", "😚", "😋", "😛", "😝", "😜", "🤪", "🤨", "🧐", "🤓", "😎", "🤩", "🥳",
    "😒", "😞", "😔", "😟", "😕", "🙁", "☹️", "😣", "😖", "😫", "😩", "🥺", "😢", "😭", "😤", "😠",
    "😡", "🤬", "🤯", "😳", "🥵", "🥶", "😱", "😨", "😰", "😥", "😓", "🤗", "🤔", "🤭", "🤫", "🤥",
    "👋", "🤚", "🖐️", "✋", "🖖", "👌", "🤌", "🤏", "✌️", "🤞", "🤟", "🤘", "🤙", "👈", "👉", "👆",
    "🖕", "👇", "☝️", "👍", "👎", "✊", "👊", "🤛", "🤜", "👏", "🙌", "👐", "🤲", "🤝", "🙏", "✍️",
    "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔", "❤️‍🔥", "❤️‍🩹", "❣️", "💕", "💞", "💓",
    "✨", "🎉", "🔥", "🚀", "💡", "💯", "🌟", "⭐", "🌈", "☀️", "🌙", "👑", "🎈", "🎁", "🎨", "🎵"
];



export default function TopNavChat({ 
    isOpen, 
    onClose,
    initialChatId,
    initialChatTitle
}: { 
    isOpen: boolean; 
    onClose: () => void;
    initialChatId?: string;
    initialChatTitle?: string;
}) {
    const { user } = useAuthenticator();
    const currentUserEmail = (user?.signInDetails?.loginId || '').toLowerCase().trim();
    const toast = useToast();

    // UI View states: 'channels' | 'chat' | 'members'
    const [currentView, setCurrentView] = useState<'channels' | 'chat' | 'members'>('channels');
    const [userOrg, setUserOrg] = useState<string>('');
    const [userRole, setUserRole] = useState<string>('Instructor');
    const [activeTab, setActiveTab] = useState<'broadcasts' | 'direct'>('broadcasts');
    const [members, setMembers] = useState<ChatUser[]>([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    
    const [activeChatId, setActiveChatId] = useState<string>('');
    const [activeChatTitle, setActiveChatTitle] = useState<string>('');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [messageInput, setMessageInput] = useState('');

    // Maximized state and members search state
    const [isMaximized, setIsMaximized] = useState(false);
    const [membersSearchQuery, setMembersSearchQuery] = useState('');

    // Read summary state (for unread badges)
    const [readSummaries, setReadSummaries] = useState<ChatReadSummary[]>([]);

    const [showPicker, setShowPicker] = useState(false);
    const [pickerTab, setPickerTab] = useState<'emoji' | 'gif' | 'sticker'>('emoji');
    const [gifQuery, setGifQuery] = useState('');
    const [gifs, setGifs] = useState<string[]>([]);
    const [loadingGifs, setLoadingGifs] = useState(false);

    const messageEndRef = useRef<HTMLDivElement>(null);
    const itemHoverBg = useColorModeValue("gray.50", "gray.800/60");
    const activeItemBg = useColorModeValue("teal.50/50", "teal.900/20");
    const textMuted = useColorModeValue("gray.500", "gray.400");
    const otherMsgColor = useColorModeValue("gray.800", "gray.100");
    const borderColor = useColorModeValue("gray.100", "gray.800");
    const otherBubbleBg = useColorModeValue("white", "rgba(255, 255, 255, 0.05)");
    const otherBubbleBorderColor = useColorModeValue("teal.50/20", "rgba(255, 255, 255, 0.05)");

    // Auto-scroll messages
    useEffect(() => {
        if (messageEndRef.current) {
            messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Fetch current user details & members on mount/open
    useEffect(() => {
        if (isOpen && currentUserEmail) {
            fetchUserOrgAndMembers();
            fetchReadSummaries();
        }
    }, [isOpen, currentUserEmail]);

    // Polling logic
    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (isOpen) {
            // Poll summaries every 3 seconds to keep badges updated when in channels list
            if (currentView === 'channels') {
                interval = setInterval(() => {
                    fetchReadSummaries();
                }, 3000);
            }
            // Poll messages every 3 seconds when inside a chat
            else if (currentView === 'chat' && activeChatId) {
                interval = setInterval(() => {
                    pollMessages();
                }, 3000);
            }
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isOpen, currentView, activeChatId]);

    useEffect(() => {
        if (!showPicker || pickerTab !== 'gif') return;
        
        setLoadingGifs(true);
        const query = gifQuery.trim().toLowerCase();
        
        // Handle cases where the JSON file is imported as default or an ES module default wrapper
        const rawGifs = PRESET_GIFS;
        const gifsArray = Array.isArray(rawGifs) 
            ? rawGifs 
            : ((rawGifs as any).default || []) as Array<{ url: string; tags: string[] }>;
        
        const filteredGifs = query
            ? gifsArray
                .filter(item => 
                    item.tags && 
                    item.tags.some(tag => tag.toLowerCase().includes(query) || query.includes(tag.toLowerCase()))
                )
                .map(item => item.url)
            : gifsArray.map(item => item.url);
            
        console.log("GIF Search:", { query, resultsCount: filteredGifs.length, totalCount: gifsArray.length });
        setGifs(filteredGifs);
        setLoadingGifs(false);
    }, [gifQuery, pickerTab, showPicker]);

    const fetchUserOrgAndMembers = async () => {
        setLoadingMembers(true);
        try {
            // 1. Get caller info to identify their org
            const callerData = await apiGetRequest<undefined, { role: string; org?: string }>(`/users/${encodeURIComponent(currentUserEmail)}`);
            const org = callerData?.org || 'Global';
            setUserOrg(org);
            const role = callerData?.role || 'Instructor';
            setUserRole(role);

            // 2. Fetch all system users and filter by same org
            const allUsers = await apiGetRequest<undefined, ChatUser[]>('/users');
            const isSystemAdmin = role === 'Super Admin' || role === 'Admin';
            const orgMembers = isSystemAdmin
                ? (allUsers || [])
                : (allUsers || []).filter(u => 
                    u.org.toLowerCase().trim() === org.toLowerCase().trim() ||
                    u.role === 'Super Admin' ||
                    u.role === 'Admin' ||
                    u.org.toLowerCase().trim() === 'global' ||
                    u.org.toLowerCase().trim() === 'system'
                  );

            // 3. Resolve user profile images in parallel
            const resolvedMembers = await Promise.all(orgMembers.map(async (member) => {
                let avatarUrl = `https://api.dicebear.com/7.x/adventurer/svg?seed=${encodeURIComponent(member.email)}`;
                if (member.picture) {
                    if (member.picture.startsWith('http')) {
                        avatarUrl = member.picture;
                    } else {
                        try {
                            const res = await apiGetRequest<undefined, { signedUrl: string }>(`/get-profile-image?mediaKeyId=${encodeURIComponent(member.picture)}`);
                            if (res?.signedUrl) {
                                avatarUrl = res.signedUrl;
                            }
                        } catch (e) {
                            console.error("Error signing avatar url for user:", member.email, e);
                        }
                    }
                }
                return {
                    ...member,
                    avatarUrl
                };
            }));

            setMembers(resolvedMembers);
        } catch (e) {
            console.error("Failed to load organization members", e);
            toast({ title: "Error", description: "Failed to load chat contacts.", status: "error", duration: 3000 });
        } finally {
            setLoadingMembers(false);
        }
    };

    const fetchReadSummaries = async () => {
        if (!currentUserEmail) return;
        try {
            const data = await apiGetRequest<undefined, ChatReadSummary[]>('/chats/read');
            if (data) {
                setReadSummaries(data);
            }
        } catch (e) {
            console.error("Failed to load unread read status summaries", e);
        }
    };

    // Poll messages silently (without triggering loading spinners)
    const pollMessages = async () => {
        if (!activeChatId) return;
        try {
            const data = await apiGetRequest<undefined, ChatMessage[]>(`/chats?chatId=${encodeURIComponent(activeChatId)}`);
            if (data) {
                setMessages(data);
            }
        } catch (e) {
            console.error("Failed to poll chat messages", e);
        }
    };

    // Open chat and trigger first load
    const handleSelectChat = async (chatId: string, title: string) => {
        setActiveChatId(chatId);
        setActiveChatTitle(title);
        setCurrentView('chat');
        setLoadingMessages(true);
        try {
            // Fetch messages
            const data = await apiGetRequest<undefined, ChatMessage[]>(`/chats?chatId=${encodeURIComponent(chatId)}`);
            setMessages(data || []);

            // Report chat read timestamp up to current timestamp
            const now = Date.now();
            await apiPostRequest('/chats/read', { chatId, timestamp: now });
            
            // Instantly clear locally in badge state
            setReadSummaries(prev => prev.map(s => s.chatId === chatId ? { ...s, lastReadTimestamp: now } : s));
        } catch (e) {
            console.error("Failed to load chat messages", e);
            toast({ title: "Error", description: "Failed to load chat history.", status: "error", duration: 3000 });
        } finally {
            setLoadingMessages(false);
        }
    };

    useEffect(() => {
        if (isOpen && initialChatId) {
            handleSelectChat(initialChatId, initialChatTitle || "Chat");
        }
    }, [isOpen, initialChatId]);

    const handleSendMessage = async () => {
        if (!messageInput.trim() || !activeChatId) return;

        const payload = {
            chatId: activeChatId,
            message: messageInput.trim()
        };

        const pendingText = messageInput.trim();
        setMessageInput('');

        try {
            const newMsg = await apiPostRequest<any, ChatMessage>('/chats', payload);
            if (newMsg) {
                setMessages(prev => {
                    if (prev.some(m => m.timestamp === newMsg.timestamp && m.senderEmail === newMsg.senderEmail)) {
                        return prev;
                    }
                    return [...prev, newMsg];
                });
            }
        } catch (e) {
            console.error("Failed to send message", e);
            setMessageInput(pendingText);
            toast({ title: "Error", description: "Message failed to send. Please check your connection.", status: "error", duration: 3000 });
        }
    };

    const handleAddEmoji = (emoji: string) => {
        setMessageInput(prev => prev + emoji);
    };

    const handleSendGif = async (gifUrl: string) => {
        const payload = {
            chatId: activeChatId,
            message: "",
            imageUrl: gifUrl
        };
        setShowPicker(false);
        try {
            const newMsg = await apiPostRequest<any, ChatMessage>('/chats', payload);
            setMessages(prev => [...prev, newMsg]);
        } catch (e) {
            console.error("Failed to send GIF", e);
            toast({ title: "Error", description: "Failed to send GIF.", status: "error", duration: 2000 });
        }
    };

    const handleSendSticker = async (stickerName: string) => {
        const payload = {
            chatId: activeChatId,
            message: "",
            imageUrl: `/stickers/${stickerName}.png`
        };
        setShowPicker(false);
        try {
            const newMsg = await apiPostRequest<any, ChatMessage>('/chats', payload);
            setMessages(prev => [...prev, newMsg]);
        } catch (e) {
            console.error("Failed to send sticker", e);
            toast({ title: "Error", description: "Failed to send sticker.", status: "error", duration: 2000 });
        }
    };

    const handleClearHistory = async (timeframe: string) => {
        try {
            setLoadingMessages(true);
            await apiDeleteRequest(`/chats?chatId=${encodeURIComponent(activeChatId)}&timeframe=${timeframe}`);
            
            // Reload chats
            const data = await apiGetRequest<undefined, ChatMessage[]>(`/chats?chatId=${encodeURIComponent(activeChatId)}`);
            setMessages(data || []);
            
            toast({ title: "Chat Cleared", description: "The message history was cleaned up successfully.", status: "success", duration: 3000 });
        } catch (e) {
            console.error("Failed to clear chat history", e);
            toast({ title: "Error", description: "Failed to clear message history.", status: "error", duration: 3000 });
        } finally {
            setLoadingMessages(false);
        }
    };



    // Check if channel has unread messages based on read summary
    const hasUnread = (chatId: string) => {
        const summary = readSummaries.find(s => s.chatId === chatId);
        if (!summary) return false;
        return summary.latestMessageTimestamp > summary.lastReadTimestamp;
    };

    const getMemberDisplayName = (m: ChatUser | undefined, emailFallback: string) => {
        if (!m) {
            const cleanEmail = emailFallback.split('@')[0];
            const emailLower = emailFallback.toLowerCase();
            if (emailLower.includes('global') || emailLower.includes('system') || emailLower.includes('superadmin') || emailLower.includes('admin')) {
                const display = cleanEmail.replace(/global/gi, 'XNav').replace(/system/gi, 'XNav');
                return display.toLowerCase().includes('xnav') ? display : `${display} @ XNav`;
            }
            return cleanEmail;
        }
        const orgLower = m.org.toLowerCase().trim();
        const name = m.nickname || m.email.split('@')[0];
        if (orgLower === 'global' || orgLower === 'system') {
            const cleanName = name.replace(/global/gi, 'XNav').replace(/system/gi, 'XNav');
            const cleanNameLower = cleanName.toLowerCase();
            if (cleanNameLower.includes('xnav') || cleanNameLower.includes('super admin') || cleanNameLower.includes('admin') || cleanNameLower.includes('superadmin')) {
                return cleanName;
            }
            return `${cleanName} @ XNav`;
        }
        return name;
    };

    const getDirectChatId = (email1: string, email2: string): string => {
        const sorted = [email1.toLowerCase().trim(), email2.toLowerCase().trim()].sort();
        return `Global#direct#${sorted[0]}#${sorted[1]}`;
    };

    // Format Course Audit Log inside chat
    const renderAuditLogCard = (logStr: string) => {
        try {
            const log = JSON.parse(logStr);
            return (
                <Box
                    p={3.5}
                    mt={1}
                    bg={useColorModeValue("teal.50/60", "teal.950/20")}
                    borderRadius="xl"
                    border="1px solid"
                    borderColor={useColorModeValue("teal.100", "teal.900/60")}
                    maxW="100%"
                    textAlign="left"
                    shadow="sm"
                >
                    <Flex align="center" gap={2} mb={1.5}>
                        <FontAwesomeIcon icon={faExclamationTriangle} color="#805ad5" size="xs" />
                        <Text fontSize="11px" fontWeight="bold" color="teal.500" textTransform="uppercase" letterSpacing="wider">
                            Course Audit Log
                        </Text>
                    </Flex>
                    <VStack align="stretch" spacing={1}>
                        <Text fontSize="xs" fontWeight="semibold">Action: <Text as="span" fontWeight="medium" color={useColorModeValue("gray.700", "gray.300")}>{log.action}</Text></Text>
                        <Text fontSize="11px" color={textMuted}>By: {log.userEmail}</Text>
                        {log.details && (
                            <Box 
                                p={2} 
                                mt={1}
                                bg={useColorModeValue("white", "gray.900/60")} 
                                borderRadius="md" 
                                border="1px solid" 
                                borderColor={borderColor}
                                maxH="80px"
                                overflowY="auto"
                            >
                                <Text fontSize="10px" fontFamily="monospace" whiteSpace="pre-wrap" wordBreak="break-word">
                                    {log.details}
                                </Text>
                            </Box>
                        )}
                    </VStack>
                </Box>
            );
        } catch {
            return (
                <Box p={2} mt={1} bg="red.50" _dark={{ bg: "red.900/10" }} borderRadius="md" fontSize="xs">
                    Failed to parse shared log data.
                </Box>
            );
        }
    };

    const renderInlineImage = (msg: ChatMessage) => {
        if (!msg.imageUrl) return null;
        const isSticker = msg.imageUrl.startsWith('/stickers/');

        if (isSticker) {
            return (
                <Box mt={2} maxW="120px" overflow="hidden">
                    <Image
                        src={msg.imageUrl}
                        maxH="100px"
                        objectFit="contain"
                        transition="all 0.2s ease"
                        _hover={{ transform: "scale(1.08)" }}
                    />
                </Box>
            );
        }

        return (
            <Box mt={2} borderRadius="xl" overflow="hidden" maxW="240px" shadow="md">
                <Image
                    src={msg.imageUrl}
                    maxH="150px"
                    objectFit="cover"
                    transition="all 0.2s ease"
                />
            </Box>
        );
    };

    const parseChatId = (chatId: string) => {
        const parts = chatId.split('#');
        const org = parts[0] || 'System';
        const displayOrg = (org.toLowerCase() === 'global' || org.toLowerCase() === 'system') ? 'XNav' : org;
        if (parts[1] === 'broadcast') {
            return {
                title: `${displayOrg} Broadcast`,
                subtitle: "Broadcast Channel",
                isBroadcast: true
            };
        } else if (parts[1] === 'direct') {
            const email1 = parts[2] || '';
            const email2 = parts[3] || '';
            const u1 = members.find(m => m.email.toLowerCase() === email1.toLowerCase());
            const u2 = members.find(m => m.email.toLowerCase() === email2.toLowerCase());
            const name1 = getMemberDisplayName(u1, email1);
            const name2 = getMemberDisplayName(u2, email2);
            return {
                title: `${name1} ↔ ${name2}`,
                subtitle: `Direct Message (${displayOrg})`,
                isBroadcast: false
            };
        }
        return { title: chatId, subtitle: "", isBroadcast: false };
    };

    const getUserAvatar = (email: string) => {
        const user = members.find(m => m.email.toLowerCase() === email.toLowerCase());
        if (user?.avatarUrl) return user.avatarUrl;
        return `https://api.dicebear.com/7.x/adventurer/svg?seed=${encodeURIComponent(email)}`;
    };

    const getActiveChatAvatar = (chatId: string) => {
        const parts = chatId.split('#');
        if (parts[1] === 'direct') {
            const email1 = parts[2] || '';
            const email2 = parts[3] || '';
            const otherEmail = email1.toLowerCase() === currentUserEmail.toLowerCase() ? email2 : email1;
            return getUserAvatar(otherEmail);
        }
        return undefined;
    };

    const filteredMembers = useMemo(() => {
        const query = membersSearchQuery.trim().toLowerCase();
        if (!query) return members;
        return members.filter(m => 
            m.email.toLowerCase().includes(query) || 
            (m.nickname && m.nickname.toLowerCase().includes(query)) ||
            m.role.toLowerCase().includes(query)
        );
    }, [members, membersSearchQuery]);

    if (!isOpen) return null;

    const currentUser = members.find(m => m.email.toLowerCase() === currentUserEmail);
    const currentUserAvatar = currentUser?.avatarUrl || getUserAvatar(currentUserEmail);

    return (
        <Portal>
            {/* MAIN MESSAGING DOCK (Channels list) */}
            <Box
                position="fixed"
                top="72px"
                right="0"
                left="auto"
                w={isMaximized ? "750px" : "380px"}
                h="calc(100vh - 72px)"
                bg={useColorModeValue("rgba(255, 255, 255, 0.9)", "rgba(20, 16, 35, 0.9)")}
                backdropFilter="blur(20px)"
                border="1px solid"
                borderColor={useColorModeValue("rgba(49, 151, 149, 0.15)", "rgba(255, 255, 255, 0.08)")}
                borderRadius="2xl 0 0 2xl"
                boxShadow="-10px 0 30px rgba(0, 0, 0, 0.06)"
                zIndex={1000}
                display="flex"
                flexDir="column"
                transition="all 0.3s ease"
            >
                {/* Header */}
                <Flex
                    px={4}
                    py={3}
                    align="center"
                    justify="space-between"
                    borderBottom="1px solid"
                    borderColor={useColorModeValue("rgba(0,0,0,0.05)", "rgba(255,255,255,0.06)")}
                >
                    <HStack spacing={2.5}>
                        <Avatar size="xs" name={currentUserEmail} src={currentUserAvatar} />
                        <VStack align="start" spacing={0}>
                            <Heading size="xs" bgGradient="linear(to-r, teal.500, blue.500)" bgClip="text" fontWeight="extrabold">
                                Messaging
                            </Heading>
                            <Text fontSize="9px" color="teal.500" fontWeight="bold">
                                Org: {(userOrg.toLowerCase() === 'global' || userOrg.toLowerCase() === 'system') ? 'XNav' : userOrg || "Loading..."}
                            </Text>
                        </VStack>
                    </HStack>
                    <HStack spacing={1}>
                        <IconButton
                            aria-label="Directory"
                            icon={<FontAwesomeIcon icon={faUsers} />}
                            variant="ghost"
                            size="xs"
                            borderRadius="full"
                            onClick={() => setCurrentView(currentView === 'members' ? 'channels' : 'members')}
                            colorScheme={currentView === 'members' ? 'teal' : 'gray'}
                        />
                        <IconButton
                            aria-label={isMaximized ? "Minimize Chat" : "Maximize Chat"}
                            icon={<FontAwesomeIcon icon={isMaximized ? faCompressAlt : faExpandAlt} />}
                            variant="ghost"
                            size="xs"
                            borderRadius="full"
                            onClick={() => setIsMaximized(!isMaximized)}
                        />
                        <IconButton
                            aria-label="Close Messaging"
                            icon={<FontAwesomeIcon icon={faTimes} />}
                            variant="ghost"
                            size="xs"
                            borderRadius="full"
                            onClick={onClose}
                        />
                    </HStack>
                </Flex>

                {/* Body: Channels or Contacts */}
                <Box flex={1} overflow="hidden" display="flex" flexDir="column">
                    {currentView === 'members' ? (
                        /* VIEW: CONTACTS DIRECTORY */
                        <Box p={3} flex={1} overflowY="auto">
                            <Heading size="2xs" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>Contacts Directory</Heading>
                            <InputGroup size="sm" mb={3}>
                                <InputLeftElement pointerEvents="none">
                                    <FontAwesomeIcon icon={faSearch} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                    placeholder="Search contacts..."
                                    value={membersSearchQuery}
                                    onChange={(e) => setMembersSearchQuery(e.target.value)}
                                    borderRadius="xl"
                                    bg={useColorModeValue("white", "gray.900/50")}
                                    focusBorderColor="teal.400"
                                />
                            </InputGroup>
                            <VStack align="stretch" spacing={2}>
                                {filteredMembers.map((m) => (
                                    <Flex
                                        key={m.email}
                                        p={2.5}
                                        align="center"
                                        borderRadius="xl"
                                        bg={m.email === currentUserEmail ? activeItemBg : undefined}
                                        _hover={{ bg: itemHoverBg }}
                                        transition="all 0.2s"
                                    >
                                        <Avatar size="sm" name={getMemberDisplayName(m, m.email)} src={m.avatarUrl} mr={3} />
                                        <VStack align="start" spacing={0} flex={1} overflow="hidden">
                                            <Text fontSize="xs" fontWeight="semibold" isTruncated>
                                                {getMemberDisplayName(m, m.email)}
                                                {m.email === currentUserEmail && " (You)"}
                                            </Text>
                                            <Text fontSize="9px" color={textMuted} isTruncated>
                                                {(m.org.toLowerCase() === 'global' || m.org.toLowerCase() === 'system') ? `${m.role} @ XNav` : `${m.role}`}
                                            </Text>
                                        </VStack>
                                        {m.email !== currentUserEmail && (
                                            <Button
                                                size="xs"
                                                colorScheme="teal"
                                                variant="solid"
                                                borderRadius="full"
                                                h="22px"
                                                fontSize="10px"
                                                px={3.5}
                                                _hover={{ transform: "scale(1.05)", shadow: "sm" }}
                                                transition="all 0.2s"
                                                onClick={() => {
                                                    const dmChatId = getDirectChatId(currentUserEmail, m.email);
                                                    handleSelectChat(dmChatId, getMemberDisplayName(m, m.email));
                                                }}
                                            >
                                                Chat
                                            </Button>
                                        )}
                                    </Flex>
                                ))}
                            </VStack>
                        </Box>
                    ) : (
                        /* VIEW: CHANNELS LIST */
                        <>
                            <Flex px={3} py={2} borderBottom="1px solid" borderColor={useColorModeValue("rgba(0,0,0,0.04)", "rgba(255,255,255,0.06)")} gap={2}>
                                <Button
                                    size="xs"
                                    variant={activeTab === 'broadcasts' ? 'solid' : 'ghost'}
                                    colorScheme="teal"
                                    borderRadius="xl"
                                    onClick={() => setActiveTab('broadcasts')}
                                    flex={1}
                                    fontSize="10px"
                                    fontWeight="bold"
                                    h="24px"
                                >
                                    Broadcasts
                                </Button>
                                <Button
                                    size="xs"
                                    variant={activeTab === 'direct' ? 'solid' : 'ghost'}
                                    colorScheme="teal"
                                    borderRadius="xl"
                                    onClick={() => setActiveTab('direct')}
                                    flex={1}
                                    fontSize="10px"
                                    fontWeight="bold"
                                    h="24px"
                                >
                                    Direct
                                </Button>
                            </Flex>

                            <VStack align="stretch" spacing={0} flex={1} overflowY="auto" py={1}>
                                {userRole === 'Super Admin' || userRole === 'Admin' ? (
                                    <Box px={3} py={2}>
                                        <Text fontSize="10px" fontWeight="bold" color="teal.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                            {activeTab === 'broadcasts' ? "Active Broadcasts" : "Active Direct Messages"}
                                        </Text>
                                        <VStack align="stretch" spacing={2}>
                                            {(() => {
                                                const filteredSummaries = readSummaries.filter(s => 
                                                    activeTab === 'broadcasts' ? s.chatId.endsWith('#broadcast') : s.chatId.includes('#direct#')
                                                );

                                                if (filteredSummaries.length === 0) {
                                                    return (
                                                        <Text py={4} fontSize="10px" color={textMuted} textAlign="center">
                                                            No active {activeTab === 'broadcasts' ? 'broadcasts' : 'conversations'}.
                                                        </Text>
                                                    );
                                                }

                                                return filteredSummaries.map((summary) => {
                                                    const parsed = parseChatId(summary.chatId);
                                                    const hasNewMsg = summary.latestMessageTimestamp > summary.lastReadTimestamp;
                                                    const isActive = activeChatId === summary.chatId;

                                                    return (
                                                        <Flex
                                                            key={summary.chatId}
                                                            p={2.5}
                                                            align="center"
                                                            justify="space-between"
                                                            borderRadius="xl"
                                                            cursor="pointer"
                                                            bg={isActive ? activeItemBg : undefined}
                                                            _hover={{ 
                                                                bg: itemHoverBg, 
                                                                transform: "translateY(-1px)"
                                                            }}
                                                            onClick={() => handleSelectChat(summary.chatId, parsed.title)}
                                                            transition="all 0.2s"
                                                        >
                                                            <HStack spacing={3} overflow="hidden" flex={1}>
                                                                <Avatar 
                                                                    size="xs" 
                                                                    name={parsed.title} 
                                                                    src={parsed.isBroadcast 
                                                                        ? undefined 
                                                                        : getActiveChatAvatar(summary.chatId)
                                                                    } 
                                                                    borderRadius="md" 
                                                                />
                                                                <VStack align="start" spacing={0} overflow="hidden">
                                                                    <Text fontSize="xs" fontWeight="semibold" isTruncated>
                                                                        {parsed.title}
                                                                    </Text>
                                                                    <Text fontSize="9px" color={textMuted} isTruncated>{parsed.subtitle}</Text>
                                                                </VStack>
                                                            </HStack>
                                                            {hasNewMsg && (
                                                                <Box w="6px" h="6px" bg="red.500" borderRadius="full" shadow="0 0 6px #e53e3e" />
                                                            )}
                                                        </Flex>
                                                    );
                                                });
                                            })()}
                                        </VStack>
                                    </Box>
                                ) : (
                                    <>
                                        {activeTab === 'broadcasts' ? (
                                            /* BROADCAST CHANNEL BANNER */
                                            <Flex
                                                mx={3}
                                                my={2}
                                                p={3.5}
                                                align="center"
                                                justify="space-between"
                                                cursor="pointer"
                                                borderRadius="xl"
                                                bgGradient={useColorModeValue(
                                                    "linear(to-br, teal.500, blue.600)", 
                                                    "linear(to-br, teal.600, blue.800)"
                                                )}
                                                color="white"
                                                _hover={{ 
                                                    transform: "translateY(-1px)",
                                                    boxShadow: "0 6px 15px -4px rgba(128, 90, 213, 0.4)" 
                                                }}
                                                onClick={() => handleSelectChat(`${userOrg}#broadcast`, "Broadcast Channel")}
                                                transition="all 0.2s"
                                                shadow="sm"
                                            >
                                                <HStack spacing={3} overflow="hidden">
                                                    <Flex w={8} h={8} borderRadius="lg" bg="rgba(255,255,255,0.2)" color="white" align="center" justify="center" shadow="sm">
                                                        <FontAwesomeIcon icon={faUsers} size="sm" />
                                                    </Flex>
                                                    <VStack align="start" spacing={0} overflow="hidden">
                                                        <Text fontWeight="extrabold" fontSize="xs" isTruncated>Broadcast Channel</Text>
                                                        <Text fontSize="9px" color="teal.100" isTruncated>Announce to all members</Text>
                                                    </VStack>
                                                </HStack>
                                                {hasUnread(`${userOrg}#broadcast`) && (
                                                    <Badge colorScheme="red" variant="solid" borderRadius="full" px={1.5} py={0.1} fontSize="8px">NEW</Badge>
                                                )}
                                            </Flex>
                                        ) : (
                                            /* DIRECT MESSAGES LIST */
                                            <Box px={3} py={2}>
                                                <Text fontSize="10px" fontWeight="bold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                                    Direct Messages
                                                </Text>
                                                <VStack align="stretch" spacing={2}>
                                                    {loadingMembers ? (
                                                        <Flex py={6} justify="center"><Spinner size="xs" color="teal.500" /></Flex>
                                                    ) : members.length <= 1 ? (
                                                        <Text py={4} fontSize="10px" color={textMuted} textAlign="center">No members found.</Text>
                                                    ) : (
                                                        members
                                                            .filter(m => m.email !== currentUserEmail)
                                                            .map((m) => {
                                                                const dmChatId = getDirectChatId(currentUserEmail, m.email);
                                                                const hasNewMsg = hasUnread(dmChatId);
                                                                const isActive = activeChatId === dmChatId;

                                                                return (
                                                                    <Flex
                                                                        key={m.email}
                                                                        p={2.5}
                                                                        align="center"
                                                                        justify="space-between"
                                                                        borderRadius="xl"
                                                                        cursor="pointer"
                                                                        bg={isActive ? activeItemBg : undefined}
                                                                        _hover={{ 
                                                                            bg: itemHoverBg, 
                                                                            transform: "translateY(-1px)"
                                                                        }}
                                                                        onClick={() => handleSelectChat(dmChatId, getMemberDisplayName(m, m.email))}
                                                                        transition="all 0.2s"
                                                                    >
                                                                        <HStack spacing={3} overflow="hidden" flex={1}>
                                                                            <Avatar size="xs" name={getMemberDisplayName(m, m.email)} src={m.avatarUrl} borderRadius="md" />
                                                                            <VStack align="start" spacing={0} overflow="hidden">
                                                                                <Text fontSize="xs" fontWeight="semibold" isTruncated>
                                                                                    {getMemberDisplayName(m, m.email)}
                                                                                </Text>
                                                                                <Text fontSize="10px" color={textMuted} isTruncated>
                                                                                     {(m.org.toLowerCase() === 'global' || m.org.toLowerCase() === 'system') ? `${m.role} @ XNav` : m.role}
                                                                                 </Text>
                                                                            </VStack>
                                                                        </HStack>
                                                                        {hasNewMsg && (
                                                                            <Box w="6px" h="6px" bg="red.500" borderRadius="full" shadow="0 0 6px #e53e3e" />
                                                                        )}
                                                                    </Flex>
                                                                );
                                                            })
                                                    )}
                                                </VStack>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </VStack>
                        </>
                    )}
                </Box>
            </Box>

            {/* FLOATING ACTIVE CONVERSATION WINDOW */}
            {activeChatId && (
                <Box
                    position="fixed"
                    bottom="20px"
                    right="400px"
                    w="380px"
                    h="calc(100vh - 112px)"
                    maxH="600px"
                    bg={useColorModeValue("rgba(255, 255, 255, 0.95)", "rgba(20, 16, 35, 0.95)")}
                    backdropFilter="blur(20px)"
                    border="1px solid"
                    borderColor={useColorModeValue("rgba(49, 151, 149, 0.15)", "rgba(255, 255, 255, 0.08)")}
                    borderRadius="2xl"
                    boxShadow="0 10px 40px rgba(0, 0, 0, 0.08)"
                    zIndex={1000}
                    display="flex"
                    flexDir="column"
                    transition="all 0.3s ease"
                >
                    {/* Header */}
                    <Flex
                        px={4}
                        py={3}
                        align="center"
                        justify="space-between"
                        borderBottom="1px solid"
                        borderColor={useColorModeValue("rgba(0,0,0,0.05)", "rgba(255,255,255,0.06)")}
                    >
                        <HStack spacing={2.5} overflow="hidden" flex={1}>
                            <Avatar size="xs" name={activeChatTitle} src={getActiveChatAvatar(activeChatId)} borderRadius="md" />
                            <VStack align="start" spacing={0} overflow="hidden">
                                <Heading size="xs" isTruncated maxW="160px">{activeChatTitle}</Heading>
                                <Text fontSize="9px" color="teal.400" fontWeight="bold">Active Chat Feed</Text>
                            </VStack>
                        </HStack>

                        <HStack spacing={1}>
                            {/* Clear History Dropdown */}
                            <Menu>
                                <MenuButton
                                    as={IconButton}
                                    icon={<FontAwesomeIcon icon={faEllipsisV} />}
                                    variant="ghost"
                                    size="xs"
                                    borderRadius="full"
                                    _hover={{ bg: useColorModeValue("teal.50", "teal.950/40"), color: "teal.500" }}
                                    _active={{ bg: useColorModeValue("teal.100", "teal.950/60") }}
                                    transition="all 0.2s"
                                />
                                <MenuList
                                    bg={useColorModeValue("rgba(255, 255, 255, 0.95)", "rgba(20, 16, 35, 0.95)")}
                                    backdropFilter="blur(20px)"
                                    border="1px solid"
                                    borderColor={useColorModeValue("teal.100/60", "teal.900/30")}
                                    borderRadius="xl"
                                    shadow="xl"
                                    p={1}
                                    minW="160px"
                                    zIndex={1100}
                                >
                                    <Text px={2.5} py={1.5} fontSize="9px" fontWeight="extrabold" color="teal.400" letterSpacing="wider" textTransform="uppercase">
                                        Clear History
                                    </Text>
                                    <MenuItem
                                        icon={<FontAwesomeIcon icon={faTrash} style={{ color: '#805ad5' }} />}
                                        fontSize="xs"
                                        borderRadius="lg"
                                        py={1.5}
                                        bg="transparent"
                                        _hover={{ bg: useColorModeValue("teal.50", "teal.950/30"), color: "teal.500" }}
                                        onClick={() => handleClearHistory('1day')}
                                    >
                                        Last 24 Hours
                                    </MenuItem>
                                    <MenuItem
                                        icon={<FontAwesomeIcon icon={faTrash} style={{ color: '#805ad5' }} />}
                                        fontSize="xs"
                                        borderRadius="lg"
                                        py={1.5}
                                        bg="transparent"
                                        _hover={{ bg: useColorModeValue("teal.50", "teal.950/30"), color: "teal.500" }}
                                        onClick={() => handleClearHistory('2days')}
                                    >
                                        Last 48 Hours
                                    </MenuItem>
                                    <MenuItem
                                        icon={<FontAwesomeIcon icon={faTrash} style={{ color: '#805ad5' }} />}
                                        fontSize="xs"
                                        borderRadius="lg"
                                        py={1.5}
                                        bg="transparent"
                                        _hover={{ bg: useColorModeValue("teal.50", "teal.950/30"), color: "teal.500" }}
                                        onClick={() => handleClearHistory('1week')}
                                    >
                                        Last 7 Days
                                    </MenuItem>
                                    <Divider my={1} />
                                    <MenuItem
                                        icon={<FontAwesomeIcon icon={faTrash} style={{ color: '#e53e3e' }} />}
                                        fontSize="xs"
                                        borderRadius="lg"
                                        py={1.5}
                                        bg="transparent"
                                        color="red.500"
                                        _hover={{ bg: useColorModeValue("red.50", "red.950/20"), color: "red.650" }}
                                        onClick={() => handleClearHistory('all')}
                                    >
                                        Clear All
                                    </MenuItem>
                                </MenuList>
                            </Menu>

                            <IconButton
                                aria-label="Close Chat"
                                icon={<FontAwesomeIcon icon={faTimes} />}
                                variant="ghost"
                                size="xs"
                                borderRadius="full"
                                onClick={() => {
                                    setActiveChatId('');
                                    fetchReadSummaries();
                                }}
                            />
                        </HStack>
                    </Flex>

                    {/* Chat Body */}
                    <Box flex={1} overflow="hidden" display="flex" flexDir="column" bg={useColorModeValue("gray.50/30", "gray.950/20")}>
                        {/* Messages Stream */}
                        <Box flex={1} overflowY="auto" p={4} display="flex" flexDir="column" gap={3}>
                            {loadingMessages ? (
                                <Flex h="100%" align="center" justify="center"><Spinner size="sm" color="teal.500" /></Flex>
                            ) : messages.length === 0 ? (
                                <Flex h="100%" direction="column" align="center" justify="center" color={textMuted} gap={1}>
                                    <FontAwesomeIcon icon={faUsers} size="lg" style={{ opacity: 0.2, marginBottom: '4px' }} />
                                    <Text fontSize="xs" fontWeight="semibold">No messages yet</Text>
                                    <Text fontSize="10px">Type a message to start.</Text>
                                </Flex>
                            ) : (
                                messages.map((msg, index) => {
                                    const isSelf = msg.senderEmail.toLowerCase() === currentUserEmail;
                                    const timeString = new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                    const senderUser = members.find(m => m.email.toLowerCase() === msg.senderEmail.toLowerCase());
                                    const displaySenderName = getMemberDisplayName(senderUser, msg.senderName);
                                    
                                    const isMediaOnly = !!msg.imageUrl && !msg.message;

                                    return (
                                        <Flex key={index} justify={isSelf ? 'flex-end' : 'flex-start'} w="100%" gap={2}>
                                            {!isSelf && (
                                                <Avatar size="xs" name={displaySenderName} src={getUserAvatar(msg.senderEmail)} />
                                            )}
                                            <Box maxW="80%">
                                                {!isSelf && (
                                                    <Text fontSize="9px" fontWeight="bold" color="teal.400" ml={1} mb={0.5}>{displaySenderName}</Text>
                                                )}
                                                <Box
                                                    bg={isMediaOnly ? "transparent" : (isSelf ? "linear-gradient(135deg, #319795, #2c7a7b)" : otherBubbleBg)}
                                                    color={isSelf ? "white" : otherMsgColor}
                                                    px={isMediaOnly ? 0 : 3}
                                                    py={isMediaOnly ? 0 : 2}
                                                    borderRadius="xl"
                                                    borderTopRightRadius={isSelf ? '4px' : 'xl'}
                                                    borderTopLeftRadius={isSelf ? 'xl' : '4px'}
                                                    fontSize="xs"
                                                    shadow={isMediaOnly ? "none" : "sm"}
                                                    border={isMediaOnly ? "none" : "1px solid"}
                                                    borderColor={isSelf ? "rgba(49, 151, 149, 0.2)" : otherBubbleBorderColor}
                                                    backdropFilter={isMediaOnly ? "none" : (isSelf ? "none" : "blur(10px)")}
                                                >
                                                    {msg.message && (
                                                        <Text style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</Text>
                                                    )}
                                                    {renderInlineImage(msg)}
                                                    {msg.sharedLog && renderAuditLogCard(msg.sharedLog)}
                                                    <Text fontSize="8px" color={isMediaOnly ? textMuted : (isSelf ? "whiteAlpha.700" : textMuted)} mt={1} textAlign={isSelf ? 'right' : 'left'}>
                                                        {timeString}
                                                    </Text>
                                                </Box>
                                            </Box>
                                        </Flex>
                                    );
                                })
                            )}
                            <div ref={messageEndRef} />
                        </Box>

                        {/* Input bar */}
                        <Box p={3} borderTop="1px solid" borderColor={borderColor} bg={useColorModeValue("white", "gray.900")}>
                            {/* Upload Preview */}
                            {/* Emoji & GIF & Sticker Picker */}
                            {showPicker && (
                                <Box
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                    borderRadius="xl"
                                    bg={useColorModeValue("rgba(255, 255, 255, 0.95)", "rgba(20, 16, 35, 0.95)")}
                                    backdropFilter="blur(20px)"
                                    boxShadow="lg"
                                    mb={2}
                                    p={3}
                                    h="260px"
                                    display="flex"
                                    flexDir="column"
                                    zIndex={1000}
                                >
                                    {/* Tabs */}
                                    <HStack spacing={4} borderBottom="1px solid" borderColor={borderColor} pb={2} mb={2}>
                                        <Button
                                            size="xs"
                                            variant={pickerTab === 'emoji' ? 'solid' : 'ghost'}
                                            colorScheme="teal"
                                            onClick={() => setPickerTab('emoji')}
                                        >
                                            Emojis
                                        </Button>
                                        <Button
                                            size="xs"
                                            variant={pickerTab === 'gif' ? 'solid' : 'ghost'}
                                            colorScheme="teal"
                                            onClick={() => setPickerTab('gif')}
                                        >
                                            GIFs
                                        </Button>
                                        <Button
                                            size="xs"
                                            variant={pickerTab === 'sticker' ? 'solid' : 'ghost'}
                                            colorScheme="teal"
                                            onClick={() => setPickerTab('sticker')}
                                        >
                                            Stickers
                                        </Button>
                                    </HStack>

                                    {/* Content */}
                                    {pickerTab === 'emoji' ? (
                                        <Box flex={1} overflowY="auto" fontSize="lg" style={{ userSelect: 'none' }}>
                                            <Grid templateColumns="repeat(8, 1fr)" gap={2}>
                                                {EMOJIS.map(emoji => (
                                                    <GridItem
                                                        key={emoji}
                                                        cursor="pointer"
                                                        onClick={() => handleAddEmoji(emoji)}
                                                        _hover={{ transform: "scale(1.15)" }}
                                                        transition="transform 0.1s"
                                                        textAlign="center"
                                                    >
                                                        {emoji}
                                                    </GridItem>
                                                ))}
                                            </Grid>
                                        </Box>
                                    ) : pickerTab === 'gif' ? (
                                        <VStack flex={1} spacing={2} align="stretch" overflow="hidden">
                                            <Input
                                                size="xs"
                                                placeholder="Search GIFs..."
                                                value={gifQuery}
                                                onChange={(e) => setGifQuery(e.target.value)}
                                            />
                                            <Box flex={1} overflowY="auto">
                                                {loadingGifs ? (
                                                    <Flex h="100%" align="center" justify="center"><Spinner size="xs" color="teal.500" /></Flex>
                                                ) : gifs.length === 0 ? (
                                                    <Text fontSize="10px" color="gray.500" textAlign="center" py={4}>No GIFs found</Text>
                                                ) : (
                                                    <Grid templateColumns="repeat(3, 1fr)" gap={1.5}>
                                                        {gifs.map((gif, i) => (
                                                            <GridItem key={i} cursor="pointer" onClick={() => handleSendGif(gif)}>
                                                                <Image
                                                                    src={gif}
                                                                    borderRadius="md"
                                                                    maxH="60px"
                                                                    w="100%"
                                                                    objectFit="cover"
                                                                    _hover={{ opacity: 0.8 }}
                                                                />
                                                            </GridItem>
                                                        ))}
                                                    </Grid>
                                                )}
                                            </Box>
                                        </VStack>
                                    ) : (
                                        <Box flex={1} overflowY="auto">
                                            <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                                                {[
                                                    { name: "hi", label: "Hi!" },
                                                    { name: "ok", label: "OK!" },
                                                    { name: "thanks", label: "Thanks!" },
                                                    { name: "congrats", label: "Congrats!" },
                                                    { name: "sad", label: "Sad..." },
                                                    { name: "omg", label: "OMG!" }
                                                ].map(sticker => (
                                                    <GridItem
                                                        key={sticker.name}
                                                        cursor="pointer"
                                                        onClick={() => handleSendSticker(sticker.name)}
                                                        _hover={{ transform: "scale(1.08)" }}
                                                        transition="transform 0.1s"
                                                        display="flex"
                                                        flexDir="column"
                                                        alignItems="center"
                                                        bg={useColorModeValue("teal.50/40", "whiteAlpha.50")}
                                                        p={2}
                                                        borderRadius="xl"
                                                        border="1px solid"
                                                        borderColor={borderColor}
                                                    >
                                                        <Image
                                                            src={`/stickers/${sticker.name}.png`}
                                                            maxH="50px"
                                                            objectFit="contain"
                                                            mb={1}
                                                        />
                                                        <Text fontSize="9px" fontWeight="bold" color="teal.400">{sticker.label}</Text>
                                                    </GridItem>
                                                ))}
                                            </Grid>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            <HStack spacing={2}>
                                <IconButton
                                    aria-label="Emojis and GIFs"
                                    icon={<FontAwesomeIcon icon={faSmile} />}
                                    variant="ghost"
                                    colorScheme="teal"
                                    size="sm"
                                    onClick={() => setShowPicker(!showPicker)}
                                />
                                <Input
                                    placeholder="Write a message..."
                                    value={messageInput}
                                    onChange={(e) => setMessageInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSendMessage();
                                    }}
                                    borderRadius="lg"
                                    bg={useColorModeValue("gray.50", "gray.800/40")}
                                    border="1px solid"
                                    borderColor={borderColor}
                                    _focus={{ borderColor: "teal.500", boxShadow: "none" }}
                                    fontSize="xs"
                                    size="sm"
                                />
                                <IconButton
                                    aria-label="Send message"
                                    icon={<FontAwesomeIcon icon={faPaperPlane} />}
                                    colorScheme="teal"
                                    borderRadius="lg"
                                    size="sm"
                                    onClick={handleSendMessage}
                                    isDisabled={!messageInput.trim()}
                                />
                            </HStack>
                        </Box>
                    </Box>
                </Box>
            )}

        </Portal>
    );
}
